import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { usePromptLogin } from '@/context/LoginModalContext';
import { Check, Crown, Zap, Store, Radio, Image as ImageIcon, Award, Percent, Gift, Shield, Wallet as WalletIcon, Ticket, ArrowLeft, Home } from 'lucide-react';

const fmt = (n) => (Number(n) || 0).toLocaleString('es-MX');

const featureIcons = {
    flash_sales_enabled: { icon: Zap, label: 'Ofertas relámpago' },
    okstore_enabled: { icon: Store, label: 'Mi OkStore' },
    oklive_enabled: { icon: Radio, label: 'OkLive Shop' },
    banners_enabled: { icon: ImageIcon, label: 'Banners' },
    okpro_enabled: { icon: Award, label: 'OkPro (boost)' },
    verified_badge: { icon: Shield, label: 'Insignia verificado' },
};

export default function PlansPage() {
    const nav = useNavigate();
    const { role, user } = useAuth();
    const promptLogin = usePromptLogin();
    const [plans, setPlans] = useState([]);
    const [billing, setBilling] = useState('monthly');
    const [checkout, setCheckout] = useState(null);
    const [couponCode, setCouponCode] = useState('');
    const [couponInfo, setCouponInfo] = useState(null);
    const [busy, setBusy] = useState(false);
    const [wallet, setWallet] = useState(null);
    const [currentPlan, setCurrentPlan] = useState(null);

    useEffect(() => {
        api.get('/plans').then(({ data }) => setPlans(data.items || [])).catch(() => {});
    }, []);

    useEffect(() => {
        if (role === 'user') {
            api.get('/wallet/me').then(({ data }) => setWallet(data.wallet)).catch(() => {});
            api.get('/me/plan').then(({ data }) => setCurrentPlan(data.subscription?.slug)).catch(() => {});
        }
    }, [role]);

    const openCheckout = async (plan) => {
        if (role !== 'user') {
            const ok = await promptLogin({ intent: 'pay' });
            if (!ok) return;
        }
        setCheckout(plan);
        setCouponCode('');
        setCouponInfo(null);
    };

    const validateCoupon = async () => {
        if (!couponCode) return;
        try {
            const { data } = await api.post('/plans/coupon/validate', { code: couponCode, plan_slug: checkout.slug });
            setCouponInfo(data);
            toast.success(`Cupón válido: -${data.discount_percent}%`);
        } catch (e) {
            setCouponInfo(null);
            toast.error(e?.response?.data?.detail || 'Cupón no válido');
        }
    };

    const price = (p) => billing === 'yearly' ? p.price_yearly_mxn : p.price_monthly_mxn;
    const finalPrice = checkout ? Math.max(0, price(checkout) - (couponInfo ? price(checkout) * (couponInfo.discount_percent / 100) : 0)) : 0;

    const subscribe = async () => {
        setBusy(true);
        try {
            const { data } = await api.post('/me/plan/subscribe', {
                slug: checkout.slug, billing, coupon_code: couponInfo ? couponCode : undefined,
            });
            toast.success(`¡Ahora tienes ${checkout.name}!`);
            setCheckout(null);
            setCurrentPlan(data.subscription.slug);
            if (data.amount_charged > 0) {
                api.get('/wallet/me').then(({ data: w }) => setWallet(w.wallet));
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error');
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen" data-testid="plans-page">
            <div className="bg-gradient-to-br from-[hsl(var(--primary))] to-red-700 text-white">
                <div className="max-w-6xl mx-auto px-4 py-10">
                    <div className="flex items-center gap-2 mb-4">
                        <Button variant="ghost" size="sm" className="text-white hover:bg-white/20" onClick={() => nav(-1)}>
                            <ArrowLeft className="h-4 w-4 mr-1" />Regresar
                        </Button>
                        <Button variant="ghost" size="sm" className="text-white hover:bg-white/20" onClick={() => nav('/')}>
                            <Home className="h-4 w-4 mr-1" />Inicio
                        </Button>
                    </div>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-80"><Crown className="h-4 w-4" />Planes OkGo</div>
                    <h1 className="text-3xl md:text-4xl font-semibold mt-2">Elige el plan ideal para tu negocio</h1>
                    <p className="text-white/80 max-w-2xl mt-2">Desde gratuito hasta ilimitado. Cambia o cancela cuando quieras. Paga con OkWallet.</p>
                    <div className="mt-6 inline-flex bg-white/10 backdrop-blur rounded-full p-1 border border-white/20">
                        <button
                            onClick={() => setBilling('monthly')}
                            className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${billing === 'monthly' ? 'bg-white text-slate-900' : 'text-white'}`}
                            data-testid="billing-monthly"
                        >Mensual</button>
                        <button
                            onClick={() => setBilling('yearly')}
                            className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${billing === 'yearly' ? 'bg-white text-slate-900' : 'text-white'}`}
                            data-testid="billing-yearly"
                        >Anual <Badge className="ml-1 bg-emerald-500 text-white text-[9px]">Ahorra 2 meses</Badge></button>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
                {plans.map(p => {
                    const featured = p.highlighted;
                    const isCurrent = currentPlan === p.slug;
                    return (
                        <Card key={p.id} className={`overflow-hidden flex flex-col ${featured ? 'border-[hsl(var(--primary))] shadow-xl md:scale-105 relative' : ''}`} data-testid={`plans-card-${p.slug}`}>
                            {featured && <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[hsl(var(--primary))] to-red-600" />}
                            <CardContent className="p-5 flex flex-col flex-1">
                                <div className="flex items-center justify-between mb-1">
                                    <div className="font-semibold text-lg">{p.name}</div>
                                    {featured && <Badge className="bg-[hsl(var(--primary))] text-white text-[10px]">Popular</Badge>}
                                    {isCurrent && <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">Actual</Badge>}
                                </div>
                                {p.tagline && <p className="text-xs text-muted-foreground mb-3">{p.tagline}</p>}
                                <div className="my-2">
                                    <div className="text-3xl font-bold">${fmt(price(p))}</div>
                                    <div className="text-xs text-muted-foreground">/{billing === 'yearly' ? 'año' : 'mes'}</div>
                                    {p.trial_days > 0 && <div className="text-xs text-emerald-700 font-medium mt-1">{p.trial_days} días gratis</div>}
                                </div>
                                <ul className="text-xs space-y-1.5 my-3 flex-1">
                                    {Object.entries(featureIcons).map(([k, f]) => {
                                        const enabled = !!p.features?.[k];
                                        const Icon = f.icon;
                                        return (
                                            <li key={k} className={`flex items-start gap-1.5 ${enabled ? '' : 'text-muted-foreground line-through opacity-50'}`}>
                                                {enabled ? <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" /> : <Icon className="h-3.5 w-3.5 shrink-0 mt-0.5" />}
                                                <span>{f.label}
                                                    {k === 'flash_sales_enabled' && p.features?.flash_sales_per_month > 0 && ` (${p.features.flash_sales_per_month === 999 ? 'ilim.' : p.features.flash_sales_per_month}/mes)`}
                                                    {k === 'oklive_enabled' && p.features?.oklive_minutes_per_month > 0 && ` (${p.features.oklive_minutes_per_month} min/mes)`}
                                                    {k === 'banners_enabled' && p.features?.banner_slots > 0 && ` (${p.features.banner_slots} slots)`}
                                                    {k === 'okpro_enabled' && p.features?.boost_credits_per_month > 0 && ` (${p.features.boost_credits_per_month} créd)`}
                                                </span>
                                            </li>
                                        );
                                    })}
                                    <li className="flex items-start gap-1.5"><Percent className="h-3.5 w-3.5 text-blue-600 shrink-0 mt-0.5" /><span>Comisión {p.features?.commission_percent}%</span></li>
                                    <li className="flex items-start gap-1.5"><Gift className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" /><span>Cashback máx {p.features?.cashback_max_percent}%</span></li>
                                    {p.features?.gratuita_max_per_year != null && (
                                        <li className="flex items-start gap-1.5 text-emerald-800 font-medium">
                                            <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
                                            <span>{p.features.gratuita_max_per_year >= 9999 ? 'Anuncios gratuitos ilimitados' : `${p.features.gratuita_max_per_year} gratuitos/año`} <span className="text-muted-foreground font-normal">(0% comisión)</span></span>
                                        </li>
                                    )}
                                    {p.features?.prime_listings_per_month != null && (
                                        <li className="flex items-start gap-1.5 text-amber-800 font-medium">
                                            <Crown className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
                                            <span>{(p.features.prime_listings_per_month === -1 || p.features.prime_listings_per_month >= 9999) ? 'Prime ilimitados/mes' : `${p.features.prime_listings_per_month} Prime/mes`}</span>
                                        </li>
                                    )}
                                </ul>
                                <Button
                                    className="w-full"
                                    variant={featured ? 'default' : 'outline'}
                                    disabled={isCurrent}
                                    onClick={() => openCheckout(p)}
                                    data-testid={`plans-contract-${p.slug}`}
                                >
                                    {isCurrent ? 'Tu plan actual' : (p.price_monthly_mxn === 0 ? 'Empezar gratis' : 'Contratar')}
                                </Button>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* Checkout dialog */}
            <Dialog open={!!checkout} onOpenChange={(v) => !v && setCheckout(null)}>
                <DialogContent data-testid="plan-checkout-dialog">
                    <DialogHeader>
                        <DialogTitle>Contratar {checkout?.name}</DialogTitle>
                    </DialogHeader>
                    {checkout && (
                        <div className="space-y-3">
                            <div className="p-3 rounded-lg bg-[hsl(var(--secondary))]">
                                <div className="flex justify-between text-sm"><span>{checkout.name}</span><span>${fmt(price(checkout))} / {billing === 'yearly' ? 'año' : 'mes'}</span></div>
                                {couponInfo && (
                                    <div className="flex justify-between text-xs text-emerald-700 mt-1">
                                        <span>Descuento {couponCode} (-{couponInfo.discount_percent}%)</span>
                                        <span>-${fmt(price(checkout) * couponInfo.discount_percent / 100)}</span>
                                    </div>
                                )}
                                <div className="border-t mt-2 pt-2 flex justify-between font-semibold"><span>Total</span><span>${fmt(finalPrice)}</span></div>
                            </div>
                            <div>
                                <label className="text-xs font-medium">Cupón (opcional)</label>
                                <div className="flex gap-2 mt-1">
                                    <Input value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} placeholder="WELCOME50" data-testid="plan-checkout-coupon" />
                                    <Button variant="outline" onClick={validateCoupon} disabled={!couponCode}><Ticket className="h-4 w-4" /></Button>
                                </div>
                            </div>
                            {wallet && (
                                <div className={`text-xs p-2 rounded flex items-center gap-2 ${wallet.balance_mxn >= finalPrice ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>
                                    <WalletIcon className="h-3.5 w-3.5" />OkWallet: ${fmt(wallet.balance_mxn)}
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setCheckout(null)}>Cancelar</Button>
                        <Button onClick={subscribe} disabled={busy} data-testid="plan-checkout-confirm">
                            {busy ? 'Procesando…' : (finalPrice === 0 ? 'Activar gratis' : `Pagar $${fmt(finalPrice)} con OkWallet`)}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
